import type { InputStateReason, Profile, User } from '../../api'
import { BILLING_PATH, PLAN_REFUSALS, refusalText } from '../../billing'
import type { LiveModel } from '../adapter'
import type { Destination } from '../types'
import { t, type Key } from '../../i18n'
import { lostFor, TWITCH_RULES_URL } from '../vertical'
import type { IconName } from '../../ui'

/*
 * Studio view rules on top of the Live model (docs/live-studio.md §3, handoff §3 stage 1):
 * which outputs need the streamer, how a row says its state in one line, the order
 * of the list, the tips, and what the stage shows. Texts come from the dictionary.
 */

/** An output the streamer has to act on (or at least know about) now. */
export function needsYou(d: Destination): boolean {
  if (!d.enabled) return false
  return d.state === 'failed' || d.state === 'reconnecting' || !!d.needsKey || !!d.needsKeyFix || !!d.issue || d.reason === 'gop_too_long'
}

/** What the fix block of an output offers (no API = no button: Retry now, other server and sets wait for stage 2). */
export type FixKind = 'key' | 'retry' | 'link' | 'settings' | 'track'

export function fixKind(d: Destination): FixKind | undefined {
  if (!needsYou(d)) return undefined
  if (d.needsKey || d.reason === 'platform_rejected_key') return 'key'
  if (d.reason === 'platform_rejected') return 'settings'
  if (d.reason === 'track_codec_unsupported') return 'track'
  if (d.issue || d.reason === 'gop_too_long') return 'link'
  return 'retry'
}

/** Short reason for a row (one line next to the logo). */
const SHORT: Partial<Record<NonNullable<Destination['reason']>, Key>> = {
  platform_rejected_key: 'row.keyRejected',
  platform_rejected: 'row.serverRejected',
  platform_unreachable: 'row.unreachable',
  platform_tls: 'row.tls',
  platform_disconnected: 'row.disconnected',
  platform_stalled: 'row.stalled',
  slow_link: 'row.slowLink',
  lagging: 'row.lagging',
  gop_too_long: 'row.gop',
  internal: 'row.internal',
}

export type Tone = 'live' | 'warn' | 'fail' | 'start' | 'idle'

/** Seconds left of the pause before the next attempt, if one is counting. */
export const retryLeft = (d: Destination, now: number) => (d.retryAt ? Math.max(0, Math.ceil((d.retryAt - now) / 1000)) : undefined)

/** The row's one status line and its colour. */
export function rowStatus(d: Destination, now: number): { text: string; tone: Tone } {
  // E1: a track note (track_missing, track_codec_unsupported) says which track, in words
  const why = d.tracks?.note?.text ?? (d.reason && SHORT[d.reason] ? t(SHORT[d.reason]!) : undefined)
  const left = retryLeft(d, now)
  // VERT-1 (mockup F; VT-17): on the main stream after the vertical input went; won't go live while it is off;
  // YouTube takes the vertical stream from the next broadcast
  const v = d.vertical?.state
  if (v === 'fallback' && d.state === 'live') {
    const mb = d.bitrateMbps !== undefined ? ` · ${d.bitrateMbps.toFixed(1)} Mbps` : ''
    return { text: `${t('vert.out.fallback', { s: lostFor(d.vertical!.lostAt, now) })}${mb}`, tone: 'warn' }
  }
  if (v === 'off' && d.enabled && d.state !== 'live' && d.state !== 'failed') return { text: t('vert.out.offVertical'), tone: 'idle' }
  if (v === 'next' && d.enabled && d.state !== 'live' && d.state !== 'failed') return { text: t('vert.out.nextTime'), tone: 'idle' }
  switch (d.state) {
    case 'live': {
      const mb = d.bitrateMbps !== undefined ? `${d.bitrateMbps.toFixed(1)} Mbps` : t('row.live')
      return why ? { text: `${why} · ${mb}`, tone: 'warn' } : { text: mb, tone: 'live' }
    }
    case 'waiting':
      return d.needsKey ? { text: t('row.needsKey'), tone: 'warn' } : { text: t('row.ready'), tone: 'idle' }
    case 'stopped':
      return { text: t('row.off'), tone: 'idle' }
    case 'starting':
      return { text: t('row.connecting'), tone: 'start' }
    case 'stopping':
      return { text: t('row.stopping'), tone: 'idle' }
    case 'unknown':
      return { text: t('row.noData'), tone: 'idle' }
    case 'reconnecting':
      return { text: `${why ?? t('row.dropped')} · ${left ? t('row.retryIn', { s: left }) : t('row.retrying')}`, tone: 'warn' }
    case 'failed':
      return { text: `${why ?? t('row.failed')}${left ? ` · ${t('row.nextTry', { s: left })}` : ''}`, tone: 'fail' }
  }
}

/** Dot / toggle colour of a row. */
export function rowTone(d: Destination): Tone {
  if (d.state === 'failed') return 'fail'
  if (d.state === 'reconnecting' || d.needsKey || d.issue || d.reason === 'gop_too_long' || d.tracks?.note?.tone === 'warn') return 'warn'
  if (d.vertical?.state === 'fallback' && d.state === 'live') return 'warn'
  if (d.state === 'live') return 'live'
  if (d.state === 'starting') return 'start'
  return 'idle'
}

/** Waiting for you → the rest → off; the server's order (id) inside each group. */
export function sortOutputs(ds: Destination[]): Destination[] {
  const rank = (d: Destination) => (!d.enabled ? 2 : needsYou(d) ? 0 : 1)
  return [...ds].sort((a, b) => rank(a) - rank(b) || a.id - b.id)
}

export type OutputsFilter = 'all' | 'need' | 'live' | 'off'

export function filterOutputs(ds: Destination[], f: OutputsFilter, q: string): Destination[] {
  const s = q.trim().toLowerCase()
  return ds.filter((d) =>
    (f === 'all' || (f === 'need' ? needsYou(d) : f === 'live' ? d.state === 'live' : !d.enabled)) &&
    (!s || d.name.toLowerCase().includes(s) || d.profileName.toLowerCase().includes(s)),
  )
}

/* ── tips ─────────────────────────────────────────────────────────────── */

export type TipKind = 'fix' | 'input' | 'todo' | 'advice' | 'ok' | 'state'
export interface Tip {
  id: string
  kind: TipKind
  title: string
  body?: string
  dest?: Destination
  /** to-do of a platform: its key page; `internal`: a page of the cabinet (Plan & usage) */
  link?: { href: string; label: string; internal?: boolean }
  /** VERT-1: the tip's own icon (mockup F) and a button that acts in place */
  icon?: IconName
  action?: TipAction
}

/** What a tip's button does (VERT-1): open the Vertical input settings, turn it on, copy the server address. */
export type TipAction = 'vertical-setup' | 'vertical-on' | 'copy-server'

/**
 * VERT-1 tips (mockup A, F; docs/vertical.md §7.5): the vertical input refused (another server) or not on the plan,
 * YouTube won't take it after the start, waiting for it, turned off while outputs ask for it; Twitch's rules and
 * what vertical outputs do when it stops.
 */
function verticalTips(m: LiveModel, profiles: Profile[] | undefined, user?: User | null): Tip[] {
  const out: Tip[] = []
  const p = m.vertical
  const wants = m.dests.filter((d) => d.enabled && d.vertical?.tag)
  if (p?.refused === 'ingest_mismatch') {
    out.push({ id: 'vert-refused', kind: 'input', title: t('vert.f.refused.t'), body: t('tip.input.ingest_mismatch'), action: 'copy-server' })
  }
  if (p?.refused === 'plan_no_vertical') {
    out.push({ id: 'vert-plan', kind: 'input', icon: 'lock', title: t('vert.f.plan.t'),
      body: `${t('plan.refuse.noVertical', { plan: user?.plan?.name ?? '' })} ${t('vert.f.plan.more')}`,
      link: { href: BILLING_PATH, label: t('vert.f.plan.a'), internal: true } })
  }
  if (m.dests.some((d) => d.enabled && d.vertical?.state === 'next')) {
    out.push({ id: 'vert-ytlate', kind: 'input', title: t('vert.f.ytLate.t'), body: t('vert.f.ytLate.b') })
  }
  if (p?.on && p.mainLive && !p.live && !p.refused && wants.length) {
    out.push({ id: 'vert-waiting', kind: 'state', title: t('vert.f.waiting.t'), body: t('vert.f.waiting.b'), action: 'vertical-setup' })
  }
  const off = m.dests.filter((d) => d.enabled && d.vertical?.state === 'off')
  if (!p?.on && off.length) {
    out.push({ id: 'vert-off', kind: 'state', title: t('vert.f.off.t'), body: off.length === 1 ? t('vert.f.off.b1') : t('vert.f.off.b', { n: off.length }), action: 'vertical-on' })
  }
  const twitch = m.dests.some((d) => d.enabled && d.platform === 'twitch' && !d.vertical)
  if (twitch && (p?.on || wants.length)) {
    out.push({ id: 'vert-twitch', kind: 'advice', icon: 'outputs', title: t('vert.tip.twitch.t'), body: t('vert.tip.twitch.b'), link: { href: TWITCH_RULES_URL, label: t('vert.tip.twitch.a') } })
  }
  if (p?.on && m.summary.live && wants.some((d) => d.vertical?.label !== 'main')) {
    out.push({ id: 'vert-follow', kind: 'advice', icon: 'route', title: t('vert.tip.follow.t'), body: t('vert.tip.follow.b') })
  }
  void profiles
  return out
}

/** Input reasons the streamer fixes in the encoder (or with us): "Now". */
const INPUT_NOW: InputStateReason[] = ['key_revoked', 'stream_suspended', 'account_blocked', 'ingest_mismatch', 'codec_unsupported', 'bitrate_over_limit', 'unstable']
const INPUT_TIP: Record<string, Key> = {
  key_revoked: 'tip.input.key_revoked',
  stream_suspended: 'tip.input.stream_suspended',
  account_blocked: 'tip.input.account_blocked',
  ingest_mismatch: 'tip.input.ingest_mismatch',
  codec_unsupported: 'tip.input.codec_unsupported',
  bitrate_over_limit: 'tip.input.bitrate_over_limit',
  unstable: 'tip.input.unstable',
}

/**
 * Now (outputs and input that need the streamer) → Before you go live (OBS, the
 * platforms where Go live is pressed by hand) → To make it better (hideable).
 */
export function buildTips(m: LiveModel, profiles: Profile[] | undefined, opts: { dropped: boolean; user?: User | null }): Tip[] {
  const out: Tip[] = []
  const reason = m.input?.state_reason
  const app = m.input?.app
  if (app?.required) {
    const v = app.min_version ?? app.latest ?? ''
    const name = app.name ?? app.id
    const when = app.enforce_after ? new Date(app.enforce_after) : undefined
    out.push({
      id: `app-required-${v}`, kind: 'input',
      title: app.enforced ? t('tip.app.enforced.title', { app: name, v }) : t('tip.app.required.title', { app: name, v }),
      body: app.enforced || !when ? t('tip.app.enforced.body', { app: name, have: app.version })
        : t('tip.app.required.body', { app: name, have: app.version, date: when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) }),
      link: app.download ? { href: app.download, label: t('tip.app.download', { app: name }) } : undefined,
    })
  }
  if (reason && INPUT_NOW.includes(reason)) out.push({ id: `input-${reason}`, kind: 'input', title: t(INPUT_TIP[reason]!) })
  // refused by the plan or an account hold (design §4.3): what happened and the plan page
  if (reason && (PLAN_REFUSALS as readonly string[]).includes(reason)) {
    const title = refusalText(reason, opts.user)
    if (title) out.push({ id: `input-${reason}`, kind: 'input', title, link: opts.user?.plan ? { href: BILLING_PATH, label: t('plan.seePlan'), internal: true } : undefined })
  }
  if (opts.dropped) out.push({ id: 'dropped', kind: 'input', title: t('tip.dropped.title'), body: t('tip.dropped.body') })
  const vt = verticalTips(m, profiles, opts.user)
  for (const d of m.dests.filter(needsYou)) out.push({ id: `fix-${d.id}`, kind: 'fix', title: d.name, dest: d })
  out.push(...vt.filter((x) => x.kind !== 'advice'))

  const on = m.dests.filter((d) => d.enabled)
  if (!m.summary.live && !opts.dropped) {
    out.push({ id: 'obs', kind: 'todo', title: t('tip.obs.title'), body: on.length ? t('tip.obs.body') : t('tip.obs.noOutputs') })
    const seen = new Set<string>()
    for (const d of on) {
      const p = profiles?.find((x) => x.id === d.profile)
      if (!p?.go_live_note || seen.has(p.id)) continue
      seen.add(p.id)
      out.push({ id: `golive-${p.id}`, kind: 'todo', title: t('tip.goLive.title', { platform: p.name }), body: p.go_live_note })
    }
  }

  const proto = m.source.protocol
  if (m.summary.live && proto === 'rtmp') out.push({ id: 'rtmp', kind: 'advice', title: t('tip.rtmp.title'), body: t('tip.rtmp.body') })
  if (m.summary.live && m.summary.keyframeSec && m.summary.keyframeSec > 2.05) {
    out.push({ id: 'gop', kind: 'advice', title: t('tip.gop.title'), body: t('tip.gop.body', { s: m.summary.keyframeSec.toFixed(1) }) })
  }
  const srt = m.source.srt
  if (srt && srt.resent !== undefined && srt.resent > 0 && (srt.late ?? 0) === 0) {
    out.push({ id: 'srt', kind: 'ok', title: t('tip.srt.title', { n: srt.resent }), body: t('tip.srt.body') })
  }
  if (app && app.newer && !app.required && app.latest) {
    const name = app.name ?? app.id
    out.push({
      id: `app-newer-${app.latest}`, kind: 'advice', title: t('tip.app.newer.title', { app: name, v: app.latest }),
      body: t('tip.app.newer.body', { have: app.version }),
      link: app.download ? { href: app.download, label: t('tip.app.download', { app: name }) } : undefined,
    })
  }
  out.push(...vt.filter((x) => x.kind === 'advice'))
  if (m.summary.live && m.source.reconnects > 0) {
    out.push({ id: 'reconnects', kind: 'advice', title: t('tip.reconnects.title', { n: m.source.reconnects }), body: t('tip.reconnects.body') })
  }
  return out
}

/** What the stage shows (data-state of the stage). */
export type StageState = 'wizard' | 'live' | 'dropped' | 'prelive' | 'unknown'

/**
 * OBS dropped during the broadcast: not sending now, but either an output is still
 * held on air (source grace) or this page saw the stream live a moment ago. There is
 * no countdown: the user API has no hold deadline yet (stage 2, source_grace_until).
 */
export function isDropped(m: LiveModel, sawLive: boolean): boolean {
  if (m.summary.live || m.source.unknown) return false
  return sawLive || m.dests.some((d) => d.state === 'live')
}
